import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  SimpleChanges,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { InputTextModule } from 'primeng/inputtext';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil, map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import {
  DataTableComponent,
  TableColumn,
  PaginationEvent,
} from '@app/shared/components/data-table/data-table.component';
import { AppButtonComponent } from '@app/shared/components/app-button/app-button.component';
import { LogicTransferService } from '../../logic-transfer.service';

export interface StockSelectionItem {
  spuCode: string;
  spuName: string;
  skuCode: string;
  skuName: string;
  stockNum: number;
  /** HELD ("Đã giữ"): reservedNum + preemptionNum + lockNum, sourced from IMS. */
  heldNum: number;
  availableQuantity: number; // sourced from IMS
  warehouseCode?: string;
  warehouseName?: string;
}

@Component({
  selector: 'app-stock-selection-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    TableModule,
    CheckboxModule,
    ToastModule,
    InputTextModule,
    TranslateModule,
    DataTableComponent,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './stock-selection-dialog.component.html',
  styleUrl: './stock-selection-dialog.component.css',
})
export class StockSelectionDialogComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() warehouseCode: string = '';
  @Input() existingSkuCodes: string[] = []; // SKU codes already added
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<StockSelectionItem[]>();

  private readonly logicTransferService = inject(LogicTransferService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly destroy$ = new Subject<void>();

  loading = signal(false);
  stockItems = signal<StockSelectionItem[]>([]);
  selectedItems = signal<StockSelectionItem[]>([]);
  totalRecords = signal(0);
  currentPage = signal(1);
  pageSize = signal(20);
  searchForm = signal<Record<string, string>>({
    productName: '',
    productCode: '',
    skuCode: '',
  });
  appliedSearchParams = signal<Record<string, string>>({});

  searchFields = [
    {
      key: 'productName',
      label: 'logic_transfer.stock_dialog.search.product_name',
      placeholder: 'logic_transfer.stock_dialog.search.product_name_placeholder',
    },
    {
      key: 'productCode',
      label: 'logic_transfer.stock_dialog.search.product_code',
      placeholder: 'logic_transfer.stock_dialog.search.product_code_placeholder',
    },
    {
      key: 'skuCode',
      label: 'logic_transfer.stock_dialog.search.sku_code',
      placeholder: 'logic_transfer.stock_dialog.search.sku_code_placeholder',
    },
  ];

  // Table columns
  tableColumns: TableColumn[] = [
    {
      label: 'logic_transfer.stock_dialog.columns.spu_code',
      prop: 'spuCode',
      width: 150,
      type: 'text',
      align: 'left',
    },
    {
      label: 'logic_transfer.stock_dialog.columns.spu_name',
      prop: 'spuName',
      width: 200,
      type: 'text',
      align: 'left',
    },
    {
      label: 'logic_transfer.stock_dialog.columns.sku_code',
      prop: 'skuCode',
      width: 150,
      type: 'text',
      align: 'left',
    },
    {
      label: 'logic_transfer.stock_dialog.columns.sku_name',
      prop: 'skuName',
      width: 200,
      type: 'text',
      align: 'left',
    },
    {
      label: 'logic_transfer.stock_dialog.columns.stock_num',
      prop: 'stockNum',
      width: 120,
      type: 'number',
      align: 'right',
      useGrouping: true,
    },
    {
      label: 'logic_transfer.stock_dialog.columns.reserved_num',
      prop: 'heldNum',
      width: 120,
      type: 'number',
      align: 'right',
      useGrouping: true,
    },
    {
      label: 'logic_transfer.stock_dialog.columns.available_quantity',
      prop: 'availableQuantity',
      width: 150,
      type: 'number',
      align: 'right',
      useGrouping: true,
    },
  ];

  ngOnInit(): void {
    // Load stock when component initializes if warehouseCode is available
    if (this.warehouseCode) {
      this.loadStock();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload stock when warehouseCode changes
    if (changes['warehouseCode'] && !changes['warehouseCode'].firstChange) {
      if (this.warehouseCode && this.visible) {
        this.loadStock();
      }
    }

    // Load stock when dialog opens
    if (changes['visible'] && changes['visible'].currentValue === true) {
      this.resetSearchState();
      if (this.warehouseCode) {
        this.loadStock();
      } else {
        this.messageService.add({
          severity: 'warn',
          summary: this.translateService.instant('common.warning'),
          detail: this.translateService.instant(
            'logic_transfer.stock_dialog.errors.no_warehouse_selected'
          ),
        });
      }
    }

    // Reset selection when dialog closes
    if (changes['visible'] && changes['visible'].currentValue === false) {
      this.selectedItems.set([]);
      this.resetSearchState();
    }
  }

  onSearch(): void {
    this.currentPage.set(1);
    this.appliedSearchParams.set({ ...this.searchForm() });
    this.loadStock();
  }

  onSearchReset(): void {
    this.resetSearchState();
    if (this.warehouseCode) {
      this.loadStock();
    }
  }

  private resetSearchState(): void {
    const resetForm: Record<string, string> = {
      productName: '',
      productCode: '',
      skuCode: '',
    };
    this.searchForm.set(resetForm);
    this.appliedSearchParams.set({});
    this.currentPage.set(1);
  }

  /**
   * Load stock items from service
   */
  loadStock(): void {
    if (!this.warehouseCode) {
      return;
    }

    this.loading.set(true);
    this.selectedItems.set([]);

    this.logicTransferService
      .loadStock(
        this.warehouseCode,
        this.currentPage() - 1,
        this.pageSize(),
        {
          productName: this.appliedSearchParams()['productName'],
          productCode: this.appliedSearchParams()['productCode'],
          skuCode: this.appliedSearchParams()['skuCode'],
        },
      )
      .pipe(
      map((response) => ({
        items: response.items.map((item) => {
          // availableQuantity/heldNum are computed by IMS in LogicTransferService.loadStock() -
          // not re-derived here from raw stockNum/reservedNum.
          return {
            spuCode: item.spuCode || '',
            spuName: item.spuName || '',
            skuCode: item.skuCode || '',
            skuName: item.skuName || '',
            stockNum: item.stockNum || 0,
            heldNum: item.heldNum ?? 0,
            availableQuantity: item.availableQuantity ?? 0,
            warehouseCode: item.warehouseCode,
            warehouseName: item.warehouseName,
          } as StockSelectionItem;
        }),
        total: response.total,
      })),
      catchError((error) => {
        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: this.translateService.instant(
            'logic_transfer.stock_dialog.errors.load_stock_failed'
          ),
        });
        return of({ items: [], total: 0 });
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: ({ items, total }) => {
        this.stockItems.set(items);
        this.totalRecords.set(total);
        this.loading.set(false);
      },
      error: (error) => {
        this.loading.set(false);
      },
    });
  }

  /**
   * Handle pagination change
   */
  onPageChange(event: PaginationEvent): void {
    this.currentPage.set((event.page ?? 0) + 1);
    this.loadStock();
  }

  /**
   * Handle selection change
   */
  onSelectionChange(selected: StockSelectionItem[]): void {
    this.selectedItems.set(selected);
  }

  /**
   * Check if item is already added
   */
  isItemAlreadyAdded(skuCode: string): boolean {
    return this.existingSkuCodes.includes(skuCode);
  }

  /**
   * Get selection state for row
   * Returns true if item is selected OR already added to table (to show checkmark for disabled items)
   */
  getSelectionState(item: StockSelectionItem): boolean {
    // Check if item is in selected items
    const isSelected = this.selectedItems().some(
      (selected) => selected.skuCode === item.skuCode
    );
    
    // Also check if item is already added (to show checkmark for disabled items)
    const isAlreadyAdded = this.isItemAlreadyAdded(item.skuCode);
    
    return isSelected || isAlreadyAdded;
  }

  /**
   * Check if row is disabled (already added to table)
   */
  isRowDisabled(item: StockSelectionItem): boolean {
    return this.isItemAlreadyAdded(item.skuCode);
  }

  /**
   * Handle confirm button click
   */
  onConfirm(): void {
    const selected = this.selectedItems();
    if (selected.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.warning'),
        detail: this.translateService.instant(
          'logic_transfer.stock_dialog.errors.no_item_selected'
        ),
      });
      return;
    }

    this.confirm.emit(selected);
    this.visibleChange.emit(false);
  }

  /**
   * Handle cancel button click
   */
  onCancel(): void {
    this.visibleChange.emit(false);
    this.selectedItems.set([]);
  }

  /**
   * Get dialog header
   */
  getDialogHeader(): string {
    return this.translateService.instant(
      'logic_transfer.stock_dialog.title'
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

